import Divider from "@mui/material/Divider";
import List, { ListProps } from "@mui/material/List";
import ListSubheader, { ListSubheaderProps } from "@mui/material/ListSubheader";
import { useTheme } from "@mui/material/styles";
import { CSSProperties } from "@mui/material/styles/createTypography";
import { mergeSx } from "designSystem/styles/style-functions";
import { Text } from "designSystem/index";
import React, { forwardRef, Fragment, FunctionComponent, ReactNode } from "react";

const PREFIX = "ListWithSubheader";

const classes = {
	listSection: `${PREFIX}listSection`,
	ul: `${PREFIX}ul`,
};

const rootStyles = (props, theme) => {
	return {
		backgroundColor: theme.palette.background.paper,
		maxHeight: props.listMaxHeight || 260,
		[`& .${classes.listSection}`]: {
			backgroundColor: "inherit",
		},
		[`& .${classes.ul}`]: {
			backgroundColor: "inherit",
			padding: 0,
		},
	};
};

export type ListSection = {
	/** the fragment of this section, specify icon as needed */
	sectionBody: ReactNode;

	/** the name of section/subheader */
	sectionName?: string;

	/** class applied to section text */
	sectionTextStyles?: CSSProperties;

	/** properties for subheader */
	subHeaderProps?: ListSubheaderProps;
};

export interface ListWithSubheaderProps extends ListProps {
	/** max height of the list, would become scrollable if exceed */
	listMaxHeight?: number;

	/** sections list */
	sections: ListSection[];
}

const ListWithSubheader: FunctionComponent<ListWithSubheaderProps> = forwardRef((props: ListWithSubheaderProps, ref) => {
	const { listSection, ul } = classes;
	const { sections, listMaxHeight, sx, ...otherProps } = props;
	const newStyles = rootStyles(props, useTheme());

	return (
		<List
			ref={ref}
			sx={mergeSx(sx, newStyles)}
			{...otherProps}
		>
			{sections?.map(({ sectionName, sectionBody, sectionTextStyles, subHeaderProps }, i) => (
				<Fragment key={i}>
					<li className={listSection}>
						<ul className={ul}>
							{!!sectionName && (
								<ListSubheader {...subHeaderProps}>
									<Text
										bold
										color="textPrimary"
										lineHeight={26}
										sx={sectionTextStyles}
										text={sectionName}
									/>
								</ListSubheader>
							)}
							{sectionBody}
						</ul>
					</li>
					{i !== sections.length - 1 &&
						<Divider />
					}
				</Fragment>
			))}
		</List>
	);
});

export default ListWithSubheader;